import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Button, Typography, Space, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  ExperimentOutlined,
  DatabaseOutlined,
  MessageOutlined,
  FileTextOutlined
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface Project {
  spec_id: string;
  project_name: string;
  created_by: string;
  created_at: string;
  status: 'active' | 'completed' | 'draft';
  media_count?: number;
  analysis_progress?: number;
  study_template_name?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data - replace with actual API call
    const mockProjects: Project[] = [
      {
        spec_id: '1',
        project_name: 'AIBM Fatherhood Study',
        created_by: 'researcher@nrg.com',
        created_at: '2024-01-15',
        status: 'active',
        media_count: 342,
        analysis_progress: 75,
        study_template_name: 'Media & Entertainment Analysis'
      },
      {
        spec_id: '2', 
        project_name: 'Young Storytellers Pilot',
        created_by: 'analyst@nrg.com',
        created_at: '2024-01-10',
        status: 'completed',
        media_count: 156,
        analysis_progress: 100,
        study_template_name: 'Media & Entertainment Analysis'
      },
      {
        spec_id: '3',
        project_name: 'Sports Media Representation',
        created_by: 'researcher@nrg.com',
        created_at: '2024-01-20',
        status: 'draft',
        media_count: 0,
        analysis_progress: 0,
        study_template_name: 'Print Media Analysis'
      }
    ];

    setTimeout(() => {
      setProjects(mockProjects);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'processing';
      case 'completed': return 'success';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
      render: (name: string, record: Project) => (
        <div>
          <Button 
            type="link" 
            style={{ padding: 0, height: 'auto' }}
            onClick={() => navigate(`/study-builder/${record.spec_id}`)}
          >
            {name}
          </Button>
          {record.study_template_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              Template: {record.study_template_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Media Files',
      dataIndex: 'media_count',
      key: 'media_count',
      render: (count: number) => count || 0,
    },
    {
      title: 'Progress',
      dataIndex: 'analysis_progress',
      key: 'analysis_progress',
      render: (progress: number) => `${progress || 0}%`,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Project) => (
        <Space size="small">
          <Button 
            size="small" 
            icon={<DatabaseOutlined />}
            onClick={() => navigate(`/metadata-explorer/${record.spec_id}`)}
          >
            Explore
          </Button>
          <Button 
            size="small" 
            icon={<MessageOutlined />}
            onClick={() => navigate(`/insight-console/${record.spec_id}`)}
          >
            Console
          </Button>
          <Button 
            size="small" 
            icon={<FileTextOutlined />}
            onClick={() => navigate(`/insight-generator/${record.spec_id}`)}
          >
            Reports
          </Button>
        </Space>
      ),
    },
  ];

  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => p.status === 'active').length;
  const totalMedia = projects.reduce((sum, p) => sum + (p.media_count || 0), 0);
  const avgProgress = projects.length > 0 
    ? Math.round(projects.reduce((sum, p) => sum + (p.analysis_progress || 0), 0) / projects.length)
    : 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Research Dashboard</Title>
        <Paragraph>
          Welcome to NRG Refract. Manage your content analysis projects and access the research workbench.
        </Paragraph>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Projects"
              value={totalProjects}
              prefix={<ExperimentOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Studies"
              value={activeProjects}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Media Files"
              value={totalMedia}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Progress"
              value={avgProgress}
              suffix="%"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent Projects */}
      <Card
        title="Recent Projects"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/study-builder')}
          >
            New Project
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={projects}
          rowKey="spec_id"
          loading={loading}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default Dashboard; 